/**
 * A small virtual file system for the web build.
 *
 * `expo-file-system` is synchronous where it matters (`exists`, `copySync`,
 * `textSync`, `size`) and the app relies on that: the audio cache checks for a
 * clip and copies bundled audio inline, with no await to hide the latency
 * behind. IndexedDB is async, so bytes are held in memory as the working copy
 * and mirrored to IndexedDB in the background.
 *
 * The whole store is read back into memory once at startup, before React
 * renders, which is what makes the synchronous API honest.
 *
 * URIs are stable `vfs:/…` paths, never `blob:` URLs. The database persists
 * `localUri` for images, recordings and cached audio, and a blob URL dies with
 * the page — a stored one would dangle on the next load. `resolveUri()` turns
 * a `vfs:` path into a blob URL at the point of use instead.
 */

const DB_NAME = 'aac-files';
const STORE = 'files';
const DB_VERSION = 1;
/** Past this, assume storage is unreachable and start without it. */
const OPEN_TIMEOUT_MS = 5000;

type Entry = { bytes: Uint8Array; mime: string };

const files = new Map<string, Entry>();
const directories = new Set<string>();
const objectUrls = new Map<string, string>();

let idb: IDBDatabase | null = null;

/**
 * Opening IndexedDB can hang indefinitely rather than failing — notably in an
 * installed iOS app, where neither `onsuccess` nor `onerror` ever fires and
 * `onblocked` is the only hint. Awaiting that without a deadline means the
 * app never renders at all: a white screen, and a child who cannot speak.
 *
 * So the open is raced against a timeout and resolves to null instead of
 * hanging. Null means "no durable storage this session": the app still runs,
 * entirely in memory. Nothing is written back in that state, deliberately —
 * a half-initialised store must never overwrite good data on disk.
 */
function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (db: IDBDatabase | null) => {
      if (settled) return;
      settled = true;
      resolve(db);
    };

    const timer = setTimeout(() => {
      console.warn('[vfs] IndexedDB did not open within %dms; running in memory', OPEN_TIMEOUT_MS);
      finish(null);
    }, OPEN_TIMEOUT_MS);

    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE)) {
          request.result.createObjectStore(STORE);
        }
      };
      request.onsuccess = () => {
        clearTimeout(timer);
        finish(request.result);
      };
      request.onerror = () => {
        clearTimeout(timer);
        finish(null);
      };
      // Another container holds an older version open; it will not resolve.
      request.onblocked = () => {
        clearTimeout(timer);
        finish(null);
      };
    } catch {
      clearTimeout(timer);
      finish(null);
    }
  });
}

/**
 * Load every stored file into memory. Called once from the entry point and
 * awaited before the app mounts.
 *
 * A failure here is survivable: the app runs with an empty file system, so
 * cards still speak through the TTS ladder and only cached clips and imported
 * pictures are missing. Communication must not stop because storage is
 * unavailable (private browsing blocks IndexedDB outright).
 */
export async function initVfs(): Promise<void> {
  idb = await openDb();
  if (!idb) return; // in-memory only; `persist` and `unpersist` already no-op

  await new Promise<void>((resolve) => {
    // Reading every file can itself stall; the app must start regardless.
    const readTimer = setTimeout(() => {
      console.warn('[vfs] reading the file store timed out; continuing');
      resolve();
    }, OPEN_TIMEOUT_MS);
    const done = () => {
      clearTimeout(readTimer);
      resolve();
    };
    const tx = idb!.transaction(STORE, 'readonly');
    const cursor = tx.objectStore(STORE).openCursor();
    cursor.onsuccess = () => {
      const c = cursor.result;
      if (!c) return done();
      const value = c.value as Entry;
      const path = String(c.key);
      files.set(path, value);
      rememberDirectories(path);
      c.continue();
    };
    cursor.onerror = () => done();
    tx.onerror = () => done();
  });
}

function rememberDirectories(path: string): void {
  const parts = path.split('/');
  for (let i = parts.length - 1; i > 1; i--) {
    directories.add(parts.slice(0, i).join('/'));
  }
}

/** Fire-and-forget mirror to IndexedDB. The in-memory map already answered. */
function persist(path: string, entry: Entry): void {
  if (!idb) return;
  try {
    idb.transaction(STORE, 'readwrite').objectStore(STORE).put(entry, path);
  } catch (e) {
    console.warn('[vfs] write failed', path, e);
  }
}

function unpersist(path: string): void {
  if (!idb) return;
  try {
    idb.transaction(STORE, 'readwrite').objectStore(STORE).delete(path);
  } catch (e) {
    console.warn('[vfs] delete failed', path, e);
  }
}

/** False when this session has no durable storage; the UI says so. */
export function storageAvailable(): boolean {
  return idb !== null;
}

export function normalize(path: string): string {
  return path.replace(/\/+$/, '');
}

export function exists(path: string): boolean {
  const p = normalize(path);
  return files.has(p) || directories.has(p);
}

export function isDirectory(path: string): boolean {
  return directories.has(normalize(path));
}

export function read(path: string): Uint8Array | undefined {
  return files.get(normalize(path))?.bytes;
}

export function sizeOf(path: string): number {
  return files.get(normalize(path))?.bytes.byteLength ?? 0;
}

export function write(path: string, bytes: Uint8Array, mime = guessMime(path)): void {
  const p = normalize(path);
  const entry = { bytes, mime };
  files.set(p, entry);
  rememberDirectories(p);
  revokeUrl(p);
  persist(p, entry);
}

export function makeDirectory(path: string): void {
  const p = normalize(path);
  directories.add(p);
  rememberDirectories(p + '/x');
}

export function remove(path: string): void {
  const p = normalize(path);

  if (files.delete(p)) {
    revokeUrl(p);
    unpersist(p);
    return;
  }

  // A directory: drop everything beneath it.
  const prefix = p + '/';
  for (const key of [...files.keys()]) {
    if (key.startsWith(prefix)) {
      files.delete(key);
      revokeUrl(key);
      unpersist(key);
    }
  }
  for (const dir of [...directories]) {
    if (dir === p || dir.startsWith(prefix)) directories.delete(dir);
  }
}

/** Immediate children of a directory, files and subdirectories alike. */
export function list(path: string): { name: string; isDirectory: boolean }[] {
  const prefix = normalize(path) + '/';
  const seen = new Map<string, boolean>();

  for (const key of files.keys()) {
    if (!key.startsWith(prefix)) continue;
    const rest = key.slice(prefix.length);
    const slash = rest.indexOf('/');
    if (slash === -1) seen.set(rest, false);
    else seen.set(rest.slice(0, slash), true);
  }
  for (const dir of directories) {
    if (!dir.startsWith(prefix)) continue;
    const rest = dir.slice(prefix.length);
    if (rest && !rest.includes('/')) seen.set(rest, true);
  }

  return [...seen].map(([name, isDir]) => ({ name, isDirectory: isDir }));
}

export function copy(from: string, to: string): void {
  const entry = files.get(normalize(from));
  if (!entry) throw new Error(`vfs: no such file: ${from}`);
  // Copy the buffer so a later in-place write cannot affect both paths.
  write(to, entry.bytes.slice(), entry.mime);
}

export function move(from: string, to: string): void {
  copy(from, to);
  remove(from);
}

function revokeUrl(path: string): void {
  const url = objectUrls.get(path);
  if (!url) return;
  URL.revokeObjectURL(url);
  objectUrls.delete(path);
}

const MIME: Record<string, string> = {
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  caf: 'audio/x-caf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  json: 'application/json',
  txt: 'text/plain',
};

export function guessMime(path: string): string {
  const ext = path.split('.').pop()?.toLowerCase() ?? '';
  return MIME[ext] ?? 'application/octet-stream';
}

/**
 * A URL an <img> or <audio> can actually load.
 *
 * Only `vfs:` paths are translated; anything already loadable by the browser
 * (http, https, blob, data) passes through untouched, so a remote card picture
 * or an ElevenLabs preview URL still works.
 */
export function resolveUri(uri: string): string {
  if (!uri.startsWith('vfs:')) return uri;

  const path = normalize(uri);
  const cached = objectUrls.get(path);
  if (cached) return cached;

  const entry = files.get(path);
  if (!entry) return '';

  // A fresh ArrayBuffer: Blob will not accept a view backed by a shared buffer.
  const url = URL.createObjectURL(new Blob([entry.bytes.slice().buffer], { type: entry.mime }));
  objectUrls.set(path, url);
  return url;
}

/** Pull bytes from any browser-loadable URL into the store. */
export async function importFromUrl(url: string, path: string): Promise<void> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`vfs: download failed (${response.status})`);
  const buffer = await response.arrayBuffer();
  write(path, new Uint8Array(buffer), response.headers.get('content-type') ?? guessMime(path));
}

/** Bytes for a picked File, registered under a stable path. */
export async function importFile(file: File, path: string): Promise<void> {
  const buffer = await file.arrayBuffer();
  write(path, new Uint8Array(buffer), file.type || guessMime(path));
}
